import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSearchParams } from 'react-router';

import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { formatSearchString } from '@sorare/core/src/lib/string/formatSearchString';

import type { useGallerySearchResults_cardCollection } from './__generated__/useGallerySearch.graphql.ts';

export const useGallerySearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get(SEARCH_PARAMS.QUERY) || '';
  return {
    search,
    onSearchChange: (newSearch: string) => {
      setSearchParams({ [SEARCH_PARAMS.QUERY]: newSearch }, { replace: true });
    },
  };
};

export const useGallerySearchResults = <
  T extends useGallerySearchResults_cardCollection,
>(
  search: string,
  cardCollections: Nullable<T[]>
): {
  hitType: 'collection' | 'player';
  cardCollection: T;
}[] => {
  if (!search.trim()) {
    return (
      cardCollections?.map(cardCollection => ({
        hitType: 'collection',
        cardCollection,
      })) || []
    );
  }
  return (
    cardCollections
      ?.map(
        (
          cardCollection
        ): { hitType: 'collection' | 'player'; cardCollection: T } | null => {
          const hasSearchInput = !!search.trim();
          const collectionNameHit = [
            cardCollection.anyTeam?.shortName,
            cardCollection.anyTeam?.name,
            cardCollection.anyTeam?.code,
          ].some(token =>
            formatSearchString(token)?.includes(
              formatSearchString(search) || ''
            )
          );
          const playerNameHit = cardCollection.userCardCollection?.slots?.some(
            slot =>
              formatSearchString(slot.slot?.anyPlayer?.displayName)?.includes(
                formatSearchString(search) || ''
              )
          );

          if (hasSearchInput && !collectionNameHit && !playerNameHit) {
            return null;
          }
          return {
            hitType: collectionNameHit ? 'collection' : 'player',
            cardCollection,
          };
        }
      )
      .filter(Boolean) || []
  );
};

useGallerySearchResults.fragments = {
  cardCollection: gql`
    fragment useGallerySearchResults_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        shortName
        name
        code
      }
      userCardCollection(forUserSlug: $user) {
        id
        slots {
          slug
          slot {
            id
            anyPlayer {
              slug
              displayName
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<useGallerySearchResults_cardCollection>,
};
